/**
 * SpotlightOverlay Component (Phase 19E: UI Highlighting & Guided Tours)
 *
 * Semi-transparent overlay with cutout around target UI element.
 * Shows tooltip with step title, description, and navigation controls.
 *
 * Features:
 * - i18n support: all labels from next-intl
 * - Localized step titles/descriptions (titleTr, descriptionTr)
 * - CSS transitions for smooth step changes
 * - Progress dots indicator
 * - Dynamic tooltip height measurement (no magic 150px)
 * - Centralized constants from spotlight.constants.ts (Rule 15)
 *
 * @module SpotlightOverlay
 */

'use client';

import React, { useRef, useEffect, useState } from 'react';
import { Box, Flex, Text } from '@axon/ui';
import { useLocale, useTranslations } from 'next-intl';

import type { UseSpotlightReturn } from '../hooks/useSpotlight';
import {
  SPOTLIGHT_LAYOUT,
  SPOTLIGHT_Z_INDEX,
  SPOTLIGHT_TRANSITIONS,
} from '../constants/spotlight.constants';

// ============================================================================
// Props
// ============================================================================

export interface SpotlightOverlayProps {
  spotlight: UseSpotlightReturn;
}

// ============================================================================
// Component
// ============================================================================

/**
 * SpotlightOverlay renders a semi-transparent backdrop with a
 * cutout highlighting the target element. Includes a tooltip
 * with step info and navigation controls.
 */
export function SpotlightOverlay({ spotlight }: SpotlightOverlayProps) {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    targetRect,
    isNavigating,
    stopSpotlight,
    nextStep,
    prevStep,
  } = spotlight;

  const t = useTranslations('chatbot');
  const locale = useLocale();
  const isTr = locale === 'tr';
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipHeight, setTooltipHeight] = useState(0);

  // Measure tooltip height dynamically (replaces hardcoded 150px — Rule 15)
  useEffect(() => {
    if (tooltipRef.current) {
      setTooltipHeight(tooltipRef.current.getBoundingClientRect().height);
    }
  }, [currentStep, currentStepIndex]);

  // Use a stable container with display:none instead of returning null
  // to prevent React DOM reconciliation errors (insertBefore crash)
  const visible =
    isActive && currentStep !== null && totalSteps > 0 && currentStepIndex < totalSteps;

  // Calculate cutout and tooltip positions (safe defaults when not visible)
  const hasTarget = visible && targetRect !== null;
  const cutout =
    hasTarget && targetRect
      ? {
          top: targetRect.top - SPOTLIGHT_LAYOUT.CUTOUT_PADDING,
          left: targetRect.left - SPOTLIGHT_LAYOUT.CUTOUT_PADDING,
          width: targetRect.width + SPOTLIGHT_LAYOUT.CUTOUT_PADDING * 2,
          height: targetRect.height + SPOTLIGHT_LAYOUT.CUTOUT_PADDING * 2,
        }
      : null;

  // Resolve localized step text
  const stepTitle = isTr && currentStep?.titleTr ? currentStep.titleTr : currentStep?.title;
  const stepDescription =
    isTr && currentStep?.descriptionTr ? currentStep.descriptionTr : currentStep?.description;

  // Tooltip position: below the cutout, or center of screen if no target
  const tooltipStyle: React.CSSProperties = cutout
    ? {
        position: 'fixed',
        top: cutout.top + cutout.height + SPOTLIGHT_LAYOUT.TOOLTIP_OFFSET,
        left: Math.max(
          SPOTLIGHT_LAYOUT.TOOLTIP_LEFT_MARGIN,
          Math.min(
            cutout.left,
            typeof window !== 'undefined'
              ? window.innerWidth - SPOTLIGHT_LAYOUT.TOOLTIP_RIGHT_MARGIN
              : SPOTLIGHT_LAYOUT.VIEWPORT_FALLBACK_WIDTH
          )
        ),
        zIndex: SPOTLIGHT_Z_INDEX.TOOLTIP,
        transition: `top ${SPOTLIGHT_TRANSITIONS.STEP_CHANGE} ${SPOTLIGHT_TRANSITIONS.EASING}, left ${SPOTLIGHT_TRANSITIONS.STEP_CHANGE} ${SPOTLIGHT_TRANSITIONS.EASING}`,
      }
    : {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: SPOTLIGHT_Z_INDEX.TOOLTIP,
      };

  // If tooltip would go below viewport, reposition it
  const measuredHeight = tooltipHeight || SPOTLIGHT_LAYOUT.TOOLTIP_MIN_WIDTH;
  if (cutout && typeof window !== 'undefined') {
    const wouldOverflowBelow =
      cutout.top + cutout.height + SPOTLIGHT_LAYOUT.TOOLTIP_OFFSET + measuredHeight >
      window.innerHeight;

    // Tall AND narrow elements (e.g., sidebar): position tooltip to the RIGHT
    const isTallNarrow =
      cutout.height > window.innerHeight * 0.7 && cutout.width < window.innerWidth * 0.4;

    if (isTallNarrow) {
      tooltipStyle.top = Math.max(
        SPOTLIGHT_LAYOUT.TOOLTIP_LEFT_MARGIN,
        cutout.top + cutout.height / 2 - measuredHeight / 2
      );
      tooltipStyle.left = cutout.left + cutout.width + SPOTLIGHT_LAYOUT.TOOLTIP_OFFSET;
    } else if (wouldOverflowBelow) {
      // Try above the cutout first
      const aboveTop = cutout.top - SPOTLIGHT_LAYOUT.TOOLTIP_OFFSET - measuredHeight;
      if (aboveTop > SPOTLIGHT_LAYOUT.TOOLTIP_LEFT_MARGIN) {
        tooltipStyle.top = aboveTop;
      } else {
        // Both above and below overflow — center vertically in viewport
        tooltipStyle.top = Math.max(
          SPOTLIGHT_LAYOUT.TOOLTIP_LEFT_MARGIN,
          (window.innerHeight - measuredHeight) / 2
        );
      }
    }
  }

  return (
    <div style={{ display: visible ? 'contents' : 'none' }}>
      {/* Overlay backdrop with clip-path cutout */}
      <Box
        position="fixed"
        top="0"
        left="0"
        width="100vw"
        height="100vh"
        bg="blackAlpha.600"
        zIndex={SPOTLIGHT_Z_INDEX.BACKDROP}
        onClick={stopSpotlight}
        style={
          cutout
            ? {
                clipPath: `polygon(
                  0% 0%, 0% 100%, ${cutout.left}px 100%, ${cutout.left}px ${cutout.top}px,
                  ${cutout.left + cutout.width}px ${cutout.top}px,
                  ${cutout.left + cutout.width}px ${cutout.top + cutout.height}px,
                  ${cutout.left}px ${cutout.top + cutout.height}px,
                  ${cutout.left}px 100%, 100% 100%, 100% 0%
                )`,
                transition: `clip-path ${SPOTLIGHT_TRANSITIONS.STEP_CHANGE} ${SPOTLIGHT_TRANSITIONS.EASING}`,
              }
            : undefined
        }
      />

      {/* Tooltip */}
      {currentStep && (
        <Box
          ref={tooltipRef}
          style={tooltipStyle}
          bg="white"
          borderRadius="lg"
          boxShadow="xl"
          padding="4"
          maxWidth={`${SPOTLIGHT_LAYOUT.TOOLTIP_MAX_WIDTH}px`}
          minWidth={`${SPOTLIGHT_LAYOUT.TOOLTIP_MIN_WIDTH}px`}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {/* Step counter + progress dots */}
          {totalSteps > 1 && (
            <Flex align="center" gap={2} marginBottom="1">
              <Text fontSize="xs" color="gray.500">
                {t('spotlight.step', { current: currentStepIndex + 1, total: totalSteps })}
              </Text>
              <Flex gap={1}>
                {Array.from({ length: totalSteps }, (_, i) => (
                  <Box
                    key={`dot-${i}`}
                    width="6px"
                    height="6px"
                    borderRadius="full"
                    bg={i === currentStepIndex ? 'blue.500' : 'gray.300'}
                    transition={`background ${SPOTLIGHT_TRANSITIONS.STEP_CHANGE} ${SPOTLIGHT_TRANSITIONS.EASING}`}
                  />
                ))}
              </Flex>
            </Flex>
          )}

          {/* Title */}
          <Text fontWeight="bold" fontSize="sm" marginBottom="1">
            {stepTitle}
          </Text>

          {/* Description */}
          <Text fontSize="sm" color="gray.600" marginBottom="3">
            {stepDescription}
          </Text>

          {/* Navigation in progress indicator */}
          {!hasTarget && isNavigating && (
            <Text fontSize="xs" color="blue.500" marginBottom="2">
              {t('spotlight.navigating')}
            </Text>
          )}

          {/* Element not found warning (only after navigation completes).
              Skipped for narration-only steps (empty spotlightId) where a
              center-screen tooltip with no cutout is intentional. */}
          {!hasTarget && !isNavigating && !!currentStep?.spotlightId && (
            <Text fontSize="xs" color="orange.500" marginBottom="2">
              {t('spotlight.notVisible')}
            </Text>
          )}

          {/* Navigation controls */}
          <Flex justify="between" align="center">
            <Text
              as="button"
              fontSize="xs"
              color="gray.500"
              cursor="pointer"
              onClick={stopSpotlight}
              _hover={{ color: 'gray.700' }}
            >
              {t('spotlight.close')}
            </Text>

            <Flex gap={2}>
              {currentStepIndex > 0 && (
                <Text
                  as="button"
                  fontSize="sm"
                  color="blue.500"
                  cursor="pointer"
                  onClick={prevStep}
                  _hover={{ color: 'blue.700' }}
                >
                  {t('spotlight.previous')}
                </Text>
              )}
              {currentStepIndex < totalSteps - 1 ? (
                <Text
                  as="button"
                  fontSize="sm"
                  color="blue.500"
                  fontWeight="bold"
                  cursor="pointer"
                  onClick={nextStep}
                  _hover={{ color: 'blue.700' }}
                >
                  {t('spotlight.next')}
                </Text>
              ) : (
                <Text
                  as="button"
                  fontSize="sm"
                  color="green.500"
                  fontWeight="bold"
                  cursor="pointer"
                  onClick={stopSpotlight}
                  _hover={{ color: 'green.700' }}
                >
                  {t('spotlight.done')}
                </Text>
              )}
            </Flex>
          </Flex>
        </Box>
      )}
    </div>
  );
}
